"use client";

import { useEffect, useMemo, useState, useCallback, memo, useRef } from "react";
import { useAreaMapsSearch, FacetItem } from "common/apis/mysociability-search";
import { useI18N, useModalHeight } from "common/hooks";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { Modal } from "react-responsive-modal";
import "react-responsive-modal/styles.css";
import ScrollContainer from "react-indiana-drag-scroll";
import { ModalHeader } from "@/components/common/ModalHeader";
import AnalyticsService from "@/services/analytics";
import { cn } from "@/lib/utils";

interface CityData {
  name: string;
  count: number;
}

// Type for the result map
type CountryLocationMap = Map<string, CityData[]>;

interface PopularCitiesProps {
  latitude: number;
  longitude: number;
  onCityPress: (cityName: string, countryName: string, count: number) => void;
}

// ============================================================================
// Wikipedia Image Utility
// ============================================================================

interface WikiThumbnail {
  source: string;
  width: number;
  height: number;
}

interface WikiPage {
  pageid: number;
  ns: number;
  title: string;
  thumbnail?: WikiThumbnail;
  pageimage: string;
}

interface WikiQuery {
  pages: {
    [pageId: string]: WikiPage;
  };
}

interface WikiApiResponse {
  batchcomplete: string;
  query: WikiQuery;
}

/**
 * Fetches the main image URL for a given search term from Wikipedia.
 */
const getWikipediaImage = async (
  searchTerm: string,
  thumbnailSize: number = 400,
): Promise<string | null> => {
  const WIKI_API_ENDPOINT = "https://en.wikipedia.org/w/api.php";

  const params = new URLSearchParams({
    action: "query",
    titles: searchTerm,
    prop: "pageimages",
    format: "json",
    pithumbsize: String(thumbnailSize),
    origin: "*",
    redirects: "1",
  });

  const url = `${WIKI_API_ENDPOINT}?${params.toString()}`;

  try {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Network response was not ok: ${response.statusText}`);
    }

    const data: WikiApiResponse = await response.json();
    const pages = data.query.pages;
    const pageId = Object.keys(pages)[0];

    if (!pageId || pageId === "-1") {
      return null;
    }

    const page = pages[pageId];

    if (page.thumbnail && page.thumbnail.source) {
      return page.thumbnail.source;
    }

    return null;
  } catch (error) {
    console.error("Error fetching Wikipedia image:", error);
    return null;
  }
};

// ============================================================================
// Transform Functions
// ============================================================================

// Transform facets data into country -> cities map
function transformToCountryMap(
  locations: FacetItem[] | undefined,
): CountryLocationMap {
  const countryMap = new Map<string, CityData[]>();

  if (!locations) return countryMap;

  locations.forEach((location) => {
    const lastCommaIndex = location.value.lastIndexOf(",");
    if (lastCommaIndex === -1) {
      console.warn(`Invalid format for location: ${location.value}`);
      return;
    }

    const cityName = location.value.substring(0, lastCommaIndex).trim();
    const countryName = location.value.substring(lastCommaIndex + 2).trim();

    const cityData: CityData = {
      name: cityName,
      count: location.count,
    };

    if (countryMap.has(countryName)) {
      if (countryMap.get(countryName)!.length < 10) {
        countryMap.get(countryName)!.push(cityData);
      }
    } else {
      if (countryMap.size < 2) {
        countryMap.set(countryName, [cityData]);
      }
    }
  });

  countryMap.forEach((cities) => {
    cities.sort((a, b) => b.count - a.count);
  });

  return countryMap;
}

// Transform facets data into country -> cities map (all cities, no limit)
function transformToCountryMapAll(
  locations: FacetItem[] | undefined,
): CountryLocationMap {
  const countryMap = new Map<string, CityData[]>();

  if (!locations) return countryMap;

  locations.forEach((location) => {
    const lastCommaIndex = location.value.lastIndexOf(",");
    if (lastCommaIndex === -1) {
      console.warn(`Invalid format for location: ${location.value}`);
      return;
    }

    const cityName = location.value.substring(0, lastCommaIndex).trim();
    const countryName = location.value.substring(lastCommaIndex + 2).trim();

    const cityData: CityData = {
      name: cityName,
      count: location.count,
    };

    if (countryMap.has(countryName)) {
      countryMap.get(countryName)!.push(cityData);
    } else {
      countryMap.set(countryName, [cityData]);
    }
  });

  countryMap.forEach((cities) => {
    cities.sort((a, b) => b.count - a.count);
  });

  return countryMap;
}

// ============================================================================
// City Pill Component (for Main View - horizontal scrolling)
// ============================================================================

interface CityPillProps {
  city: CityData;
  onPress: () => void;
  cityRef?: (el: HTMLButtonElement | null) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLButtonElement>) => void;
  placesLabel?: string;
}

const CityPill = memo(({ city, onPress, cityRef, onKeyDown, placesLabel }: CityPillProps) => {
  return (
    <>
      <button
        ref={cityRef}
        type="button"
        onClick={onPress}
        onKeyDown={onKeyDown}
        className="flex-shrink-0 flex items-center justify-center 
        rounded-full 
        border border-border-token-brand-minimal px-6 py-2.5 
        hover:bg-bg-brand-secondary-hover 
        active:bg-bg-brand-secondary-active
        transition-all duration-200 focus-blue
        min-w-fit"
        aria-label={`${city.name}, ${city.count} ${placesLabel}`}
      >
        <span className="label-m-medium text-fg-neutral-strong whitespace-nowrap">
          {city.name}
        </span>
      </button>
    </>
  );
});

CityPill.displayName = "CityPill";

// ============================================================================
// City Card Component (for Modal only)
// ============================================================================

interface CityCardProps {
  city: CityData;
  placesLabel: string;
  onPress: () => void;
}

const CityCard = memo(({ city, placesLabel, onPress }: CityCardProps) => {
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    const fetchImage = async () => {
      setIsLoading(true);
      // Requesting larger size for modal display
      const url = await getWikipediaImage(city.name, 200);
      if (isMounted) {
        setImageUrl(url);
        setIsLoading(false);
      }
    };

    fetchImage();

    return () => {
      isMounted = false;
    };
  }, [city.name]);

  return (
    <button
      type="button"
      onClick={onPress}
      aria-label={`${city.name}, ${city.count} ${placesLabel}`}
      className="flex flex-col items-center gap-2 p-3 
      rounded-[8px] 
      bg-bg-brand-secondary-default
      hover:bg-bg-brand-secondary-hover 
      active:bg-bg-brand-secondary-active
      transition-all duration-200 focus-blue w-full"
    >
      {/* Image Container - matching BrandsSection style */}
      <div className="w-full aspect-square rounded-xl bg-container-neutral-minimal overflow-hidden flex items-center justify-center">
        {isLoading ? (
          <div aria-hidden="true" className="w-full h-full bg-container-neutral-minimal animate-pulse" />
        ) : imageUrl ? (
          <img
            src={imageUrl}
            alt={city.name}
            className="w-full h-full object-cover"
            aria-hidden="true"
          />
        ) : (
          <div aria-hidden="true" className="w-16 h-16 rounded-full bg-neutral-300 flex items-center justify-center">
            <span className="title-m-bold text-fg-neutral-subtle">
              {city.name.charAt(0).toUpperCase()}
            </span>
          </div>
        )}
      </div>
      {/* City Info */}
      <div className="text-center w-full">
        <p className="label-m-semibold text-fg-neutral-strong">
          {city.name}
        </p>
        <p className="label-s-medium text-fg-neutral-subtle">
          {city.count} {placesLabel}
        </p>
      </div>
    </button>
  );
});

CityCard.displayName = "CityCard";

// ============================================================================
// Skeleton Component
// ============================================================================

function PopularCitiesSkeleton() {
  return (
    <div className="mt-4">
      <div className="flex items-center justify-between mb-3">
        <Skeleton height={20} width={120} />
        <Skeleton height={16} width={50} />
      </div>
      <div className="mb-4">
        <Skeleton height={16} width={80} className="mb-2" />
        <div className="flex gap-2 overflow-x-auto pb-2">
          {Array.from({ length: 4 }).map((_, index) => (
            <Skeleton
              key={`skeleton-${index}`}
              height={40}
              width={100}
              className="rounded-full flex-shrink-0"
            />
          ))}
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Main Component
// ============================================================================

export default function PopularCities({
  latitude,
  longitude,
  onCityPress,
}: PopularCitiesProps) {
  const i18n = useI18N();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { modalStyle, modalClassName } = useModalHeight();
  const cityRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const viewAllButtonRef = useRef<HTMLButtonElement>(null);
  const scrollableRef = useRef<HTMLDivElement>(null);

  const { data, isLoading, error } = useAreaMapsSearch(latitude, longitude, {
    enabled:
      latitude !== undefined &&
      longitude !== undefined &&
      !isNaN(latitude) &&
      !isNaN(longitude),
  });

  // Transform data to country -> cities map (limited for main view)
  const countriesArray = useMemo(() => {
    if (!data?.facets?.locations) return [];

    const countryMap = transformToCountryMap(data.facets.locations);
    return Array.from(countryMap.entries()).map(([country, cities]) => ({
      country,
      cities,
    }));
  }, [data]);

  // Transform data to country -> cities map (all cities for modal)
  const allCountriesArray = useMemo(() => {
    if (!data?.facets?.locations) return [];

    const countryMap = transformToCountryMapAll(data.facets.locations);
    return Array.from(countryMap.entries()).map(([country, cities]) => ({
      country,
      cities,
    }));
  }, [data]);

  useEffect(() => {
    if (error) {
      console.error("Error loading popular cities:", error);
    }
  }, [error]);

  const handleCityPress = useCallback(
    (cityName: string, countryName: string, count: number) => {
      // Track analytics
      AnalyticsService.trackGAEventHandler("popularCitiesCityClicked");
      AnalyticsService.trackMx("popularCitiesCityClicked", {
        CityName: cityName,
        CountryName: countryName,
        LocationCount: count,
      });

      onCityPress(cityName, countryName, count);
    },
    [onCityPress],
  );

  const handleViewAllClick = useCallback(() => {
    // Track analytics
    AnalyticsService.trackGAEventHandler("popularCitiesViewAllClicked");
    AnalyticsService.trackMx("popularCitiesViewAllClicked", {});

    // Open the modal
    setIsModalOpen(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
    // Return focus to the "View All" button after modal closes
    setTimeout(() => {
      viewAllButtonRef.current?.focus();
    }, 100);
  }, []);

  const handleCityClickFromModal = useCallback(
    (cityName: string, countryName: string, count: number) => {
      // Close the modal first
      setIsModalOpen(false);
      // Then perform the city click action
      handleCityPress(cityName, countryName, count);
      // Return focus to the "View All" button
      setTimeout(() => {
        viewAllButtonRef.current?.focus();
      }, 100);
    },
    [handleCityPress],
  );

  // Ensure modal is properly closed and focus is returned when unmounting
  useEffect(() => {
    return () => {
      if (isModalOpen) {
        setIsModalOpen(false);
      }
    };
  }, [isModalOpen]);

  // Focus scrollable container when modal opens for immediate keyboard scrolling
  useEffect(() => {
    if (isModalOpen && scrollableRef.current) {
      // Small delay to ensure modal is fully rendered
      setTimeout(() => {
        scrollableRef.current?.focus();
      }, 100);
    }
  }, [isModalOpen]);

  // Calculate total cities for keyboard navigation
  const totalCities = useMemo(() => {
    return countriesArray.reduce((sum, { cities }) => sum + cities.length, 0);
  }, [countriesArray]);

  // Handle keyboard navigation for city pills
  const handleCityKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLButtonElement>, index: number) => {
      if (e.key === "ArrowRight") {
        e.preventDefault();
        const nextIndex = (index + 1) % totalCities;
        cityRefs.current[nextIndex]?.focus();
      } else if (e.key === "ArrowLeft") {
        e.preventDefault();
        const prevIndex = (index - 1 + totalCities) % totalCities;
        cityRefs.current[prevIndex]?.focus();
      } else if (e.key === "Home") {
        e.preventDefault();
        cityRefs.current[0]?.focus();
      } else if (e.key === "End") {
        e.preventDefault();
        cityRefs.current[totalCities - 1]?.focus();
      }
    },
    [totalCities],
  );

  if (isLoading) {
    return <PopularCitiesSkeleton />;
  }

  if (countriesArray.length === 0) {
    return null;
  }

  const placesLabel = i18n.get("places");

  return (
    <div className="mt-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <h2 className="label-m-semibold text-fg-neutral-strong">
          {i18n.get("popularCities")}
        </h2>
        <button
          ref={viewAllButtonRef}
          type="button"
          onClick={handleViewAllClick}
          className="label-m-semibold text-fg-brand-subtle underline hover:text-fg-brand-strong transition-colors focus-blue"
          aria-label={i18n.get("viewAll")}
        >
          {i18n.get("viewAll")}
        </button>
      </div>

      {/* Cities List - Horizontal Pills (like native) */}
      <div className="space-y-2">
        {countriesArray.map(({ country, cities }, countryIndex) => {
          // Calculate the starting index for this country's cities
          const startIndex = countriesArray
            .slice(0, countryIndex)
            .reduce((sum, { cities }) => sum + cities.length, 0);

          return (
            <div key={country}>
              <span className="label-s-medium text-fg-neutral-subtle">
                {country}
              </span>

              {/* Horizontal scroll for small screens */}
              <div role="group" aria-label={`${country} cities`} className="md:hidden">
                <ScrollContainer className="flex gap-3 py-2">
                  {cities.map((city, cityIndex) => {
                    const globalIndex = startIndex + cityIndex;
                    return (
                      <CityPill
                        key={city.name}
                        city={city}
                        placesLabel={placesLabel}
                        onPress={() =>
                          handleCityPress(city.name, country, city.count)
                        }
                        cityRef={(el) => (cityRefs.current[globalIndex] = el)}
                        onKeyDown={(e) => handleCityKeyDown(e, globalIndex)}
                      />
                    );
                  })}
                </ScrollContainer>
              </div>

              {/* Flex wrap layout for medium screens and up */}
              <div className="hidden md:flex md:flex-wrap gap-3 py-2" role="group" aria-label={`${country} cities`}>
                {cities.map((city, cityIndex) => {
                  const globalIndex = startIndex + cityIndex;
                  return (
                    <CityPill
                      key={city.name}
                      city={city}
                      placesLabel={placesLabel}
                      onPress={() =>
                        handleCityPress(city.name, country, city.count)
                      }
                      cityRef={(el) => (cityRefs.current[globalIndex] = el)}
                      onKeyDown={(e) => handleCityKeyDown(e, globalIndex)}
                    />
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {/* All Cities Modal */}
      {isModalOpen && (
      <Modal
        focusTrapped={true}
        center
        blockScroll={false}
        animationDuration={0}
        open={isModalOpen}
        onClose={handleCloseModal}
        onAnimationEnd={() => {
          // Ensure focus returns after modal animation completes
          if (!isModalOpen && viewAllButtonRef.current) {
            viewAllButtonRef.current.focus();
          }
        }}
        classNames={{
          modal:
            "bg-[transparent] p-0 focus:outline-0 shadow-none w-[95vw] md:w-[720px]",
          closeButton: "hidden",
        }}
      >
        <section
          style={modalStyle}
          className={cn(modalClassName, "w-full md:w-[720px] overflow-hidden")}
          role="dialog"
          aria-modal="true"
          aria-labelledby="cities-modal-title"
        >
          {/* Modal Header */}
          <ModalHeader title={i18n.get("city")} onClose={handleCloseModal} titleId="cities-modal-title" />

          <div className="flex flex-col flex-1 w-full bg-bg-brand-secondary-default rounded-b-2xl overflow-hidden">
            {/* Modal Content - Scrollable */}
            {/* eslint-disable jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/no-noninteractive-tabindex */}
            <div
              ref={scrollableRef}
              tabIndex={0}
              role="region"
              aria-label="Cities list"
              className="flex-1 overflow-y-auto p-4 max-h-[calc(100vh-200px)] focus-blue"
              onKeyDown={(e) => {
                if (e.key === ' ' && e.target === e.currentTarget) {
                  e.preventDefault();
                  e.currentTarget.scrollBy({
                    top: e.currentTarget.clientHeight * 0.8,
                    behavior: 'smooth',
                  });
                }
              }}
            >
              <div className="space-y-6">
                {allCountriesArray.map(({ country, cities }) => (
                  <div key={country}>
                    {/* Country Header with Count */}
                    <h4 className="label-m-medium text-fg-neutral-strong mb-3">
                      {country} ({cities.length})
                    </h4>
                    {/* Cities Grid - matching BrandsSection layout */}
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      {cities.map((city) => (
                        <CityCard
                          key={city.name}
                          city={city}
                          placesLabel={placesLabel}
                          onPress={() =>
                            handleCityClickFromModal(
                              city.name,
                              country,
                              city.count,
                            )
                          }
                        />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            {/* eslint-enable jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/no-noninteractive-tabindex */}
          </div>
        </section>
      </Modal>
      )}
    </div>
  );
}
